import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "../components/Skeleton.js";
import { VehicleDetailModal } from "../components/VehicleDetailModal.js";
import {
  CriticalBatteryAlert,
  type CriticalVehicleInput,
} from "../components/CriticalBatteryAlert.js";

/**
 * Cache de endereços geocodificados por bucket lat/lon (4 casas ≈ 11m).
 * Um Map compartilhado entre todas as linhas da tabela evita duplicar
 * requisições quando dois ônibus estão a menos de ~11m um do outro.
 */
const geocodeCache = new Map<string, string>();

/**
 * Fila serializada de geocode. Sem isso, uma tela com 60 veículos dispara
 * 60 fetches paralelos e satura o rate-limit interno (429/503). Um único
 * worker consome a fila com backoff de 120ms entre chamadas.
 */
const geocodeQueue: Array<() => Promise<void>> = [];
let geocodeWorkerActive = false;

async function geocodeWorker(): Promise<void> {
  if (geocodeWorkerActive) return;
  geocodeWorkerActive = true;
  try {
    while (geocodeQueue.length > 0) {
      const job = geocodeQueue.shift()!;
      try {
        await job();
      } catch {
        /* ignora — job cuida do próprio erro */
      }
      // Nominatim aceita ~1 req/s; nosso servidor tem cache mas primeira carga
      // atinge o upstream direto. 500ms deixa margem confortável.
      await new Promise((res) => setTimeout(res, 500));
    }
  } finally {
    geocodeWorkerActive = false;
  }
}

function enqueueGeocode(job: () => Promise<void>): void {
  geocodeQueue.push(job);
  void geocodeWorker();
}

function gpsBucket(lat: number, lon: number): string {
  return `${lat.toFixed(4)},${lon.toFixed(4)}`;
}

/**
 * Célula GPS: mostra endereço (rua + número) via /api/geocode/reverse,
 * fallback pra coordenadas. Clique abre /app/mapa-frota?focus=<vin>.
 */
function GpsCell({
  lat,
  lon,
  vin,
  onNavigate,
}: {
  lat: number | null;
  lon: number | null;
  vin: string;
  onNavigate: (vin: string) => void;
}) {
  const [address, setAddress] = useState<string | null>(null);
  useEffect(() => {
    if (lat === null || lon === null) return;
    const bucket = gpsBucket(lat, lon);
    const cached = geocodeCache.get(bucket);
    if (cached) {
      setAddress(cached);
      return;
    }
    let alive = true;
    enqueueGeocode(async () => {
      if (!alive) return;
      // Re-check cache — bucket vizinho pode ter resolvido enquanto esperava.
      const nowCached = geocodeCache.get(bucket);
      if (nowCached) {
        setAddress(nowCached);
        return;
      }
      try {
        const r = await fetch(`/api/geocode/reverse?lat=${lat}&lon=${lon}`, {
          credentials: "include",
        });
        if (!r.ok) return;
        const body: { formatted: string } = await r.json();
        if (!alive) return;
        geocodeCache.set(bucket, body.formatted);
        setAddress(body.formatted);
      } catch {
        /* silencia; fallback pra coords */
      }
    });
    return () => {
      alive = false;
    };
  }, [lat, lon]);

  if (lat === null || lon === null) {
    return <span style={{ color: "var(--nx-mute)" }}>—</span>;
  }
  return (
    <button
      type="button"
      onClick={() => onNavigate(vin)}
      title="Ver no mapa ao vivo"
      style={{
        background: "none",
        border: "none",
        padding: 0,
        color: "var(--nx-orange-500)",
        font: "inherit",
        fontSize: ".72rem",
        cursor: "pointer",
        textAlign: "left",
        textDecoration: "underline",
        maxWidth: 220,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "inline-block",
      }}
    >
      {address ?? `${lat.toFixed(4)}, ${lon.toFixed(4)}`}
    </button>
  );
}

/**
 * Tela Telemetria Frota — snapshot atual de cada veiculo eletrico da frota,
 * alimentado pelo servico nxvolt-signalstore (consumer Kafka do Signal Store
 * da EvoBus/Daimler Truck). Uma linha por chassi, marcada "ativo" quando
 * teve msg nos ultimos 2 min.
 */

interface Stats {
  total: number;
  ativos: number;
  carregando: number;
  ligados: number;
  soc_medio: number | null;
  soc_min: number | null;
  soc_max: number | null;
}

interface VehicleRow {
  local_prefix: string | null;
  chassi_vin: string;
  last_seen: string | null;
  msg_count: number;
  updated_at: string | null;
  soc_pct: number | null;
  range_km: number | null;
  energy_available_kwh: number | null;
  cell_temp_avg: number | null;
  cell_temp_max: number | null;
  speed_kmh: number | null;
  odometer_km: number | null;
  ignition: boolean | null;
  handbrake: boolean | null;
  gps_lat: number | null;
  gps_lon: number | null;
  gps_sats: number | null;
  plug_charging: boolean | null;
  charging_power_kw: number | null;
  motor_torque_nm: number | null;
  air_temp_outside: number | null;
  air_temp_inside: number | null;
  is_active: boolean;
}

function fmt(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined) return "—";
  return Number(n).toFixed(decimals);
}

function socColor(soc: number | null): string {
  if (soc === null || soc === undefined) return "var(--nx-mute)";
  if (soc < 20) return "var(--nx-danger)";
  if (soc < 50) return "var(--nx-warn)";
  return "var(--nx-success)";
}

export function TelemetriaFrotaPage() {
  const [data, setData] = useState<{ vehicles: VehicleRow[]; stats: Stats | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(new Date());
  // Filtro client-side por prefixo (ou chassi). Case-insensitive, substring.
  const [filter, setFilter] = useState("");
  // VIN do veículo aberto no modal de detalhes; null = modal fechado.
  const [selectedVin, setSelectedVin] = useState<string | null>(null);
  /**
   * Ordenação da tabela: clique em cabeçalho alterna asc → desc → sem sort.
   * `sortKey` é a chave da linha do VehicleRow (com casos especiais como
   * "prefix"/"estado"/"gps"/"updated"). null = ordem original (última atualização).
   */
  type SortKey =
    | "prefix"
    | "chassi"
    | "estado"
    | "soc"
    | "range"
    | "speed"
    | "odom"
    | "temp"
    | "gps"
    | "updated";
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const toggleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      // asc → desc → limpar
      setSortKey(null);
    }
  };
  const sortIndicator = (key: SortKey): string => {
    if (sortKey !== key) return " ⇅";
    return sortDir === "asc" ? " ↑" : " ↓";
  };
  // Auto-refresh (30s) — off por padrão pra não gastar tráfego móvel.
  // Persistido em localStorage.
  const [autoRefresh, setAutoRefresh] = useState<boolean>(() => {
    try {
      return localStorage.getItem("nx.telemetria.autorefresh") === "1";
    } catch {
      return false;
    }
  });
  // Popup crítico (SOC ≤ 5%). O detector abre no máximo um popup por vez;
  // "snooze" gravado em sessionStorage evita reabrir por 5 min.
  const [criticalTarget, setCriticalTarget] =
    useState<CriticalVehicleInput | null>(null);
  const navigate = useNavigate();
  // Guarda VINs já sinalizados neste ciclo pra não spammar o dispatcher em
  // cada re-render. Limpa quando o state some.
  const criticalDispatchedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    try {
      localStorage.setItem("nx.telemetria.autorefresh", autoRefresh ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [autoRefresh]);
  // Filtro client-side por status ('' = todos)
  const [statusFilter, setStatusFilter] = useState<"" | "rodando" | "carregando" | "parado" | "offline">("");

  useEffect(() => {
    // AbortController cancela request pendente em desmontagem/re-run do effect.
    // Sem isso, um payload que chega tarde sobrescreve estado depois do unmount
    // (warning "state update on unmounted component") ou reintroduz dados
    // antigos se o filtro/rota mudar durante o fetch.
    const controller = new AbortController();

    const load = async () => {
      try {
        const r = await fetch("/api/telemetria-frota", {
          credentials: "include",
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        if (controller.signal.aborted) return;
        setData(json);
        setError(null);
        setNow(new Date());
      } catch (e) {
        if (e instanceof Error && e.name === "AbortError") return;
        setError(String(e));
      }
    };

    void load();
    // Interval só dispara load quando aba visível — evita tráfego morto em
    // background e reduz drain de bateria em mobile.
    // Base sempre 15s (heartbeat suave); se autoRefresh ativado, sobe pra 30s
    // como reforço explícito solicitado pelo usuário na tela.
    const interval = autoRefresh ? 30_000 : 15_000;
    const t = setInterval(() => {
      if (!document.hidden) void load();
    }, interval);
    // Ao voltar de aba oculta, faz catch-up imediato pra não esperar o
    // próximo tick do interval.
    const onVis = () => {
      if (!document.hidden) void load();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      controller.abort();
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [autoRefresh]);

  // Detector de bateria crítica (SOC ≤ 5%): abre o popup pra 1 veículo
  // por vez. Respeita snooze via sessionStorage (5 min por VIN).
  useEffect(() => {
    if (!data || criticalTarget) return;
    const now = Date.now();
    for (const v of data.vehicles) {
      if (!v.is_active) continue;
      if (v.soc_pct === null || v.soc_pct > 5) continue;
      if (criticalDispatchedRef.current.has(v.chassi_vin)) continue;
      let snoozed = false;
      try {
        const until = sessionStorage.getItem(`nx-crit-snooze-${v.chassi_vin}`);
        if (until && Number(until) > now) snoozed = true;
      } catch {
        /* ignore */
      }
      if (snoozed) continue;
      criticalDispatchedRef.current.add(v.chassi_vin);
      setCriticalTarget({
        vin: v.chassi_vin,
        prefix: v.local_prefix,
        socPct: v.soc_pct,
        rangeKm: v.range_km,
        lat: v.gps_lat,
        lon: v.gps_lon,
      });
      break;
    }
  }, [data, criticalTarget]);

  // Ao fechar o popup, limpa o dispatched pra permitir alertar sobre outros
  // veículos que caíram no critério enquanto o operador atendia este.
  const handleCloseCritical = () => {
    criticalDispatchedRef.current.clear();
    setCriticalTarget(null);
  };
  const navigateToMap = (vin: string) => {
    navigate(`/app/mapa_frota?focus=${encodeURIComponent(vin)}`);
  };

  if (error && !data) {
    return (
      <div className="nx-card">
        <div className="nx-error">Erro ao carregar telemetria: {error}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <div className="nx-page-head">
          <div className="nx-page-head__meta">
            <span className="nx-page-head__eyebrow">Frota Elétrica</span>
            <h1>Telemetria Frota</h1>
            <p className="nx-page-head__sub">Carregando dados do Signal Store…</p>
          </div>
        </div>
        <div className="nx-kpi-grid" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={`s-${i}`} variant="kpi" />
          ))}
        </div>
        <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
          <Skeleton variant="row" count={8} />
        </div>
      </div>
    );
  }

  const s = data.stats;
  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__meta">
          <span className="nx-page-head__eyebrow">Frota Elétrica</span>
          <h1>Telemetria Frota</h1>
          <p className="nx-page-head__sub">
            Signal Store · {data.vehicles.length} chassi cadastrados · atualizado{" "}
            {now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </p>
        </div>
        <div className="nx-page-head__actions" style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: ".85rem",
              color: "var(--nx-mute)",
              cursor: "pointer",
            }}
            title="Recarrega automaticamente a lista a cada 30s"
          >
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto refresh (30s)
          </label>
        </div>
      </div>

      <div
        className="nx-kpi-grid"
        role="status"
        aria-live="polite"
        aria-atomic="false"
      >
        <div className="nx-kpi nx-kpi--navy">
          <div className="nx-kpi__label">Total cadastrado</div>
          <div className="nx-kpi__value">{s?.total ?? 0}</div>
          <div className="nx-kpi__hint">chassis Mercedes-Benz</div>
        </div>
        <div className="nx-kpi nx-kpi--success">
          <div className="nx-kpi__label">Ativos agora</div>
          <div className="nx-kpi__value">{s?.ativos ?? 0}</div>
          <div className="nx-kpi__hint">msg nos últimos 2 min</div>
        </div>
        <div className="nx-kpi nx-kpi--cyan">
          <div className="nx-kpi__label">Ligados</div>
          <div className="nx-kpi__value">{s?.ligados ?? 0}</div>
          <div className="nx-kpi__hint">com ignição</div>
        </div>
        <div className="nx-kpi nx-kpi--orange">
          <div className="nx-kpi__label">Carregando</div>
          <div className="nx-kpi__value">{s?.carregando ?? 0}</div>
          <div className="nx-kpi__hint">plug conectado</div>
        </div>
      </div>

      {s && s.soc_medio !== null && (
        <div
          className="nx-card"
          style={{
            marginTop: "var(--nx-space-4)",
            marginBottom: "var(--nx-space-6)",
            padding: "1rem 1.2rem",
            display: "flex",
            gap: "1.5rem",
            alignItems: "center",
            fontSize: ".9rem",
          }}
        >
          <div>
            SOC médio: <b style={{ color: socColor(s.soc_medio) }}>{fmt(s.soc_medio)}%</b>
          </div>
          <div>
            Mín: <b style={{ color: socColor(s.soc_min) }}>{fmt(s.soc_min)}%</b>
          </div>
          <div>
            Máx: <b style={{ color: socColor(s.soc_max) }}>{fmt(s.soc_max)}%</b>
          </div>
        </div>
      )}

      <div className="nx-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "var(--nx-space-3)",
            gap: "1rem",
            flexWrap: "wrap",
          }}
        >
          <h2 style={{ fontSize: "var(--nx-text-md)", margin: 0 }}>Snapshot por veículo</h2>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
            <label
              htmlFor="tf-filter"
              style={{ fontSize: ".75rem", color: "var(--nx-mute)", whiteSpace: "nowrap" }}
            >
              Prefixo:
            </label>
            <input
              id="tf-filter"
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="ex: 00015"
              className="nx-input"
              style={{ width: "9rem", fontFamily: "var(--nx-font-mono, monospace)" }}
              autoComplete="off"
            />
            <label
              htmlFor="tf-status"
              style={{ fontSize: ".75rem", color: "var(--nx-mute)", whiteSpace: "nowrap", marginLeft: ".5rem" }}
            >
              Status:
            </label>
            <select
              id="tf-status"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as "" | "rodando" | "carregando" | "parado" | "offline")
              }
              className="nx-input"
              style={{ width: "9rem" }}
            >
              <option value="">Todos</option>
              <option value="rodando">Rodando</option>
              <option value="carregando">Carregando</option>
              <option value="parado">Parado</option>
              <option value="offline">Offline</option>
            </select>
            {(filter || statusFilter) && (
              <button
                type="button"
                onClick={() => {
                  setFilter("");
                  setStatusFilter("");
                }}
                className="nx-btn nx-btn--ghost"
                style={{ fontSize: ".75rem", padding: ".3rem .6rem" }}
                aria-label="Limpar filtros"
              >
                Limpar
              </button>
            )}
          </div>
        </div>
        {data.vehicles.length === 0 ? (
          <p style={{ color: "var(--nx-mute)", margin: 0 }}>
            Nenhum veículo capturado ainda. O consumer coleta continuamente.
          </p>
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  {/* Cabeçalhos clicáveis: cada th vira botão que alterna
                      asc → desc → sem sort. Indicador ⇅/↑/↓ à direita. */}
                  {([
                    ["prefix", "id", "Prefixo"],
                    ["chassi", "", "Chassi"],
                    ["estado", "", "Estado"],
                    ["soc", "num", "SOC"],
                    ["range", "num", "Autonomia"],
                    ["speed", "num", "Vel."],
                    ["odom", "num", "Odom."],
                    ["temp", "num", "Temp. cél."],
                    ["gps", "", "GPS"],
                    ["updated", "", "Atualizado"],
                  ] as Array<[SortKey, string, string]>).map(([key, cls, label]) => (
                    <th
                      key={key}
                      className={cls || undefined}
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort(key)}
                      title="Clique para ordenar"
                    >
                      {label}
                      <span
                        style={{
                          fontSize: ".8em",
                          color:
                            sortKey === key
                              ? "var(--nx-orange-500)"
                              : "var(--nx-mute)",
                        }}
                      >
                        {sortIndicator(key)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const filtered = data.vehicles.filter((v) => {
                    // Filtro de texto (prefixo OU chassi)
                    if (filter.trim()) {
                      const q = filter.trim().toLowerCase();
                      const hit =
                        (v.local_prefix ?? "").toLowerCase().includes(q) ||
                        v.chassi_vin.toLowerCase().includes(q);
                      if (!hit) return false;
                    }
                    // Filtro de status. Precedência bate com a coluna Estado da tabela.
                    if (statusFilter === "offline") return !v.is_active;
                    if (statusFilter === "carregando") return v.is_active && !!v.plug_charging;
                    if (statusFilter === "rodando")
                      return v.is_active && !v.plug_charging && !!v.ignition;
                    if (statusFilter === "parado")
                      return v.is_active && !v.plug_charging && !v.ignition;
                    return true;
                  });

                  // Ordenação. Nulls sempre no fim (independente da direção)
                  // para o operador nunca ver a coluna "—" no topo.
                  if (sortKey !== null) {
                    const dir = sortDir === "asc" ? 1 : -1;
                    const cmpNum = (a: number | null, b: number | null): number => {
                      if (a === null && b === null) return 0;
                      if (a === null) return 1; // null sempre no fim
                      if (b === null) return -1;
                      return (a - b) * dir;
                    };
                    const cmpStr = (a: string | null, b: string | null): number => {
                      if (!a && !b) return 0;
                      if (!a) return 1;
                      if (!b) return -1;
                      return a.localeCompare(b) * dir;
                    };
                    const stateOf = (v: VehicleRow): number => {
                      // Ordenação lógica: carregando(0) → rodando(1) → parado(2) → offline(3)
                      if (!v.is_active) return 3;
                      if (v.plug_charging) return 0;
                      if (v.ignition) return 1;
                      return 2;
                    };
                    filtered.sort((a, b) => {
                      switch (sortKey) {
                        case "prefix":
                          return cmpStr(a.local_prefix, b.local_prefix);
                        case "chassi":
                          return cmpStr(a.chassi_vin, b.chassi_vin);
                        case "estado":
                          return (stateOf(a) - stateOf(b)) * dir;
                        case "soc":
                          return cmpNum(a.soc_pct, b.soc_pct);
                        case "range":
                          return cmpNum(a.range_km, b.range_km);
                        case "speed":
                          return cmpNum(a.speed_kmh, b.speed_kmh);
                        case "odom":
                          return cmpNum(a.odometer_km, b.odometer_km);
                        case "temp":
                          return cmpNum(a.cell_temp_avg, b.cell_temp_avg);
                        case "gps":
                          // Sort GPS por lat (proxy geográfico N-S)
                          return cmpNum(a.gps_lat, b.gps_lat);
                        case "updated":
                          return cmpStr(a.updated_at, b.updated_at);
                        default:
                          return 0;
                      }
                    });
                  }

                  if (filtered.length === 0) {
                    // Bug pego em review Frontend-A: tabela ficava com <tbody>
                    // vazio quando filtros zeravam o resultado. Estado invisível
                    // parecia "loading" ao usuário. Agora avisa explicitamente.
                    return (
                      <tr>
                        <td
                          colSpan={10}
                          style={{
                            textAlign: "center",
                            color: "var(--nx-mute)",
                            padding: "1.5rem",
                          }}
                        >
                          Nenhum veículo bate o filtro atual.
                        </td>
                      </tr>
                    );
                  }
                  return filtered.map((v) => {
                  // Padrão STALE: quando v.is_active=false (sem msg nos
                  // últimos 2 min), o snapshot no banco é o ÚLTIMO recebido.
                  // Não é "SOC=100% agora" — é "SOC=100% quando parou de
                  // reportar". Aplicamos opacity + cinza + rótulo pra o
                  // operador não confiar em número congelado.
                  const stale = !v.is_active;
                  const staleStyle: React.CSSProperties = stale
                    ? { opacity: 0.55, filter: "grayscale(0.6)" }
                    : {};
                  return (
                  <tr key={v.chassi_vin} title={stale ? "Sem sinal do veículo há mais de 2 minutos — dados são o último snapshot recebido" : undefined}>
                    <td className="id mono" style={{ fontWeight: 700 }}>
                      {v.local_prefix ? (
                        <button
                          type="button"
                          onClick={() => setSelectedVin(v.chassi_vin)}
                          className="nx-link-btn"
                          title="Ver detalhes + mapa (últimos 30 min)"
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            color: "var(--nx-orange-500)",
                            font: "inherit",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          {v.local_prefix}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="mono" style={{ fontSize: ".75rem" }}>
                      {v.chassi_vin}
                    </td>
                    <td>
                      {!v.is_active ? (
                        <span className="nx-badge" style={{ background: "var(--nx-mute-bg)", color: "var(--nx-mute)" }}>
                          Offline
                        </span>
                      ) : v.plug_charging ? (
                        <span className="nx-badge nx-badge--charging">Carregando</span>
                      ) : v.ignition ? (
                        <span
                          className="nx-badge"
                          style={{
                            background: "color-mix(in srgb, var(--nx-success-500) 13%, transparent)",
                            color: "var(--nx-success-500)",
                          }}
                        >
                          Rodando
                        </span>
                      ) : (
                        <span
                          className="nx-badge"
                          style={{
                            background: "color-mix(in srgb, var(--nx-info-500) 13%, transparent)",
                            color: "var(--nx-info-500)",
                          }}
                        >
                          Parado
                        </span>
                      )}
                    </td>
                    <td className="num mono" style={staleStyle}>
                      <span style={{ color: stale ? "var(--nx-mute)" : socColor(v.soc_pct), fontWeight: 700 }}>
                        {v.soc_pct !== null ? `${fmt(v.soc_pct)}%` : "—"}
                        {stale && v.soc_pct !== null && (
                          <span style={{ fontWeight: 400, fontSize: ".7rem", marginLeft: 4, opacity: 0.8 }}>
                            (último)
                          </span>
                        )}
                      </span>
                      {!stale && v.soc_pct !== null && v.soc_pct < 10 && (
                        <span
                          className="nx-badge"
                          style={{
                            marginLeft: 6,
                            background:
                              v.soc_pct <= 5
                                ? "color-mix(in srgb, var(--nx-danger) 15%, transparent)"
                                : "color-mix(in srgb, var(--nx-warning) 15%, transparent)",
                            color:
                              v.soc_pct <= 5
                                ? "var(--nx-danger)"
                                : "var(--nx-warning)",
                            fontSize: ".65rem",
                            padding: "1px 6px",
                          }}
                          title={
                            v.soc_pct <= 5
                              ? "Bateria crítica — despachar reforço"
                              : "Bateria baixa — monitorar"
                          }
                        >
                          {v.soc_pct <= 5 ? "⚠ crítico" : "⚠ baixo"}
                        </span>
                      )}
                    </td>
                    <td className="num mono" style={staleStyle}>{v.range_km !== null ? `${fmt(v.range_km, 0)} km` : "—"}</td>
                    <td className="num mono" style={staleStyle}>{v.speed_kmh !== null ? `${fmt(v.speed_kmh, 0)}` : "—"}</td>
                    <td className="num mono" style={staleStyle}>
                      {v.odometer_km != null
                        ? v.odometer_km.toLocaleString("pt-BR", { maximumFractionDigits: 0 })
                        : "—"}
                    </td>
                    <td className="num mono" style={staleStyle}>{v.cell_temp_avg !== null ? `${fmt(v.cell_temp_avg)}°C` : "—"}</td>
                    <td className="mono" style={{ fontSize: ".72rem" }}>
                      <GpsCell
                        lat={v.gps_lat}
                        lon={v.gps_lon}
                        vin={v.chassi_vin}
                        onNavigate={navigateToMap}
                      />
                    </td>
                    <td
                      style={{
                        fontSize: ".75rem",
                        color: stale
                          ? "var(--nx-warning, var(--nx-warn))"
                          : "var(--nx-mute)",
                        fontWeight: stale ? 600 : 400,
                      }}
                    >
                      {v.updated_at
                        ? new Date(v.updated_at).toLocaleTimeString("pt-BR", {
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          })
                        : "—"}
                      {stale && (
                        <div
                          style={{
                            fontSize: ".65rem",
                            fontStyle: "italic",
                            color: "var(--nx-warning, var(--nx-warn))",
                          }}
                        >
                          sem sinal
                        </div>
                      )}
                    </td>
                  </tr>
                  );
                  });
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {selectedVin && (
        <VehicleDetailModal
          vin={selectedVin}
          onClose={() => setSelectedVin(null)}
        />
      )}
      {criticalTarget && (
        <CriticalBatteryAlert
          vehicle={criticalTarget}
          onClose={handleCloseCritical}
          onNavigateToMap={(vin) => {
            handleCloseCritical();
            navigateToMap(vin);
          }}
        />
      )}
    </div>
  );
}
